import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { uropayService } from '../../services/uropayService';
import { useAuth } from '../../contexts/AuthContext';

const POLL_INTERVAL_MS = 4000;
const POLL_TIMEOUT_MS = 90000;

const UroPaySuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshProfile } = useAuth();
  const [phase, setPhase] = useState('awaiting_reference');
  const [referenceInput, setReferenceInput] = useState('');
  const [loading, setLoading] = useState(false);
  const pollIntervalRef = useRef(null);
  const pollTimeoutRef = useRef(null);

  const planId = searchParams.get('plan') || 'monthly';
  const referenceNumber = searchParams.get('referenceNumber') || searchParams.get('reference') || '';
  const uroPayOrderId = searchParams.get('uroPayOrderId') || '';
  const amount = searchParams.get('amount') || '';
  const status = searchParams.get('status') || 'success';
  const pendingIdFromUrl = searchParams.get('pendingId') || '';

  const stopPolling = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
  };

  const startVerification = async (refNo) => {
    const cleanRef = String(refNo || '').trim();
    if (cleanRef.length < 10) {
      setPhase('invalid_reference');
      return;
    }

    setLoading(true);
    setPhase('polling');
    try {
      const pendingId =
        pendingIdFromUrl ||
        (typeof window !== 'undefined' ? window.sessionStorage?.getItem('uropayPendingId') || '' : '');

      const { data: returned, error: returnedError } = await uropayService.setReturned({
        planId,
        pendingId: pendingId || undefined,
        referenceNumber: cleanRef,
        uroPayOrderId,
        amount,
        status,
      });

      if (returnedError) throw returnedError;

      if (returned?.activated) {
        window.sessionStorage?.removeItem('uropayPendingId');
        refreshProfile?.();
        setPhase('activated');
        return;
      }

      const poll = async () => {
        const { data } = await uropayService.checkActivation(cleanRef);
        if (data?.activated) {
          stopPolling();
          window.sessionStorage?.removeItem('uropayPendingId');
          refreshProfile?.();
          setPhase('activated');
        }
      };

      await poll();
      pollIntervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
      pollTimeoutRef.current = setTimeout(() => {
        stopPolling();
        setPhase('failed');
      }, POLL_TIMEOUT_MS);
    } catch (error) {
      setPhase('failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (referenceNumber) {
      startVerification(referenceNumber);
    }
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceNumber]);

  useEffect(() => {
    if (phase !== 'activated') return;
    const t = setTimeout(() => navigate('/subscription-payment', { replace: true }), 2000);
    return () => clearTimeout(t);
  }, [phase, navigate]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-lg px-4 py-10">
        <div className="bg-card rounded-xl shadow-warm-lg p-6 md:p-8 text-center">
          {(phase === 'awaiting_reference' || phase === 'invalid_reference') && (
            <>
              <h1 className="text-2xl font-heading font-bold mb-2">Confirm UPI Reference</h1>
              <p className="text-muted-foreground mb-4">
                Enter your UPI reference number to verify and activate your subscription.
              </p>
              <input
                type="text"
                value={referenceInput}
                onChange={(e) => setReferenceInput(e.target.value)}
                placeholder="Enter UPI reference number"
                className="w-full border border-border bg-background rounded-lg px-4 py-3 mb-4"
              />
              {phase === 'invalid_reference' && (
                <p className="text-error text-sm mb-4">Please enter a valid reference number.</p>
              )}
              <Button onClick={() => startVerification(referenceInput)} disabled={loading} fullWidth>
                {loading ? 'Verifying...' : 'Verify Payment'}
              </Button>
            </>
          )}

          {phase === 'polling' && (
            <>
              <Icon name="Loader2" size={48} className="animate-spin mx-auto mb-3 text-primary" />
              <p className="text-muted-foreground">Verifying your payment...</p>
            </>
          )}

          {phase === 'failed' && (
            <>
              <Icon name="XCircle" size={56} className="mx-auto mb-3 text-error" />
              <h2 className="text-xl font-heading font-bold mb-2">Verification failed</h2>
              <p className="text-muted-foreground mb-5">
                Please retry. If amount was debited, share your UPI reference with support.
              </p>
              <Button onClick={() => navigate('/subscription-payment')} fullWidth>
                Back to Subscription
              </Button>
            </>
          )}

          {phase === 'activated' && (
            <>
              <Icon name="CheckCircle" size={56} className="mx-auto mb-3 text-success" />
              <h2 className="text-xl font-heading font-bold mb-2">Payment Successful</h2>
              <p className="text-muted-foreground">Your subscription is now active.</p>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default UroPaySuccessPage;
